import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, TrendingDown, DollarSign, Percent, FileText, 
  Users, ArrowLeft, Calendar, ChevronDown, Download
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPremiumPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [vatSummary, setVatSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [dashboardRes, chartRes, vatRes, kpisRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard-premium/overview?period=${period}`, { headers }),
        fetch(`${API_URL}/api/dashboard-premium/revenue-chart?months=12`, { headers }),
        fetch(`${API_URL}/api/dashboard-premium/vat-summary?period=${period}`, { headers }),
        fetch(`${API_URL}/api/dashboard-premium/kpis`, { headers })
      ]);

      const dashboardData = await dashboardRes.json();
      const chartDataRes = await chartRes.json();
      const vatData = await vatRes.json();
      const kpisData = await kpisRes.json();

      setDashboard(dashboardData);
      setChartData(chartDataRes.chart_data || []);
      setVatSummary(vatData);
      setKpis(kpisData.kpis);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pieData = dashboard ? [
    { name: 'Payées', value: dashboard.paid_invoices, color: '#10B981' },
    { name: 'En attente', value: dashboard.pending_invoices, color: '#F59E0B' },
    { name: 'En retard', value: dashboard.overdue_invoices, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="dashboard-premium-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Dashboard Premium</h1>
            <p className="text-slate-500 text-sm">Vue d'ensemble de votre activité</p>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
          {['month', 'quarter', 'year'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid={`period-${p}`}
            >
              {p === 'month' ? 'Mois' : p === 'quarter' ? 'Trimestre' : 'Année'}
            </button>
          ))}
        </div>
      </div>

      {/* Period Label */}
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-slate-400" />
        <span className="text-slate-600 font-medium">{dashboard?.period_label}</span>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Revenue */}
        <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Chiffre d'affaires</p>
                <p className="text-3xl font-bold text-blue-600" data-testid="total-revenue">
                  {dashboard?.total_revenue?.toLocaleString('fr-FR')} €
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {dashboard?.revenue_evolution >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-sm font-medium ${
                    dashboard?.revenue_evolution >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {dashboard?.revenue_evolution > 0 ? '+' : ''}{dashboard?.revenue_evolution}%
                  </span>
                  <span className="text-xs text-slate-400">vs période précédente</span>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-blue-100">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VAT Collected */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">TVA collectée</p>
                <p className="text-2xl font-bold text-violet-600" data-testid="vat-collected">
                  {dashboard?.vat_collected?.toLocaleString('fr-FR')} €
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  À reverser à l'État
                </p>
              </div>
              <div className="p-3 rounded-xl bg-violet-50">
                <Percent className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Rate */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taux de paiement</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="payment-rate">
                  {dashboard?.payment_rate}%
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {dashboard?.paid_invoices}/{dashboard?.total_invoices} factures payées
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50">
                <FileText className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Amount */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold text-amber-600">
                  {dashboard?.pending_amount?.toLocaleString('fr-FR')} €
                </p>
                <p className="text-xs text-red-500 mt-1">
                  dont {dashboard?.overdue_amount?.toLocaleString('fr-FR')} € en retard
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50">
                <Users className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Revenue Chart */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Évolution du chiffre d'affaires</CardTitle>
            <CardDescription>12 derniers mois</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                    tickFormatter={(value) => `${value}€`}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value.toLocaleString('fr-FR')} €`, 'CA']}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#3B82F6" 
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Status Pie */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Répartition des factures</CardTitle>
            <CardDescription>{dashboard?.total_invoices} factures au total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {pieData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-sm text-slate-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* VAT Summary & Top Clients */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* VAT Summary */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Récapitulatif TVA</CardTitle>
                <CardDescription>{vatSummary?.period}</CardDescription>
              </div>
              <Badge className="bg-violet-100 text-violet-700">
                {vatSummary?.period_type === 'quarter' ? 'Trimestre' : 
                 vatSummary?.period_type === 'year' ? 'Année' : 'Mois'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                <span className="text-slate-600">TVA collectée</span>
                <span className="font-semibold text-emerald-600">
                  {vatSummary?.vat_collected?.total?.toLocaleString('fr-FR')} €
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <span className="text-slate-600">TVA déductible</span>
                <span className="font-semibold text-amber-600">
                  - {vatSummary?.vat_deductible?.toLocaleString('fr-FR')} €
                </span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-800">TVA à reverser</span>
                  <span className={`text-xl font-bold ${
                    vatSummary?.vat_to_pay >= 0 ? 'text-blue-600' : 'text-emerald-600'
                  }`}>
                    {vatSummary?.vat_to_pay?.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
              
              {/* VAT by rate */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-slate-500 mb-2">Détail par taux</p>
                <div className="space-y-2">
                  {Object.entries(vatSummary?.vat_collected?.by_rate || {})
                    .filter(([_, value]) => value > 0)
                    .map(([rate, value]) => (
                      <div key={rate} className="flex justify-between text-sm">
                        <span className="text-slate-600">TVA {rate}%</span>
                        <span className="text-slate-800">{value.toLocaleString('fr-FR')} €</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Top 5 clients</CardTitle>
            <CardDescription>Par chiffre d'affaires sur la période</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboard?.top_clients?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.top_clients.map((client, index) => (
                  <div 
                    key={client.name} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-slate-400' :
                        index === 2 ? 'bg-amber-600' :
                        'bg-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-slate-800">{client.name}</span>
                    </div>
                    <span className="font-semibold text-blue-600">
                      {client.revenue.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPIs */}
      {kpis && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Indicateurs clés (année en cours)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-sm text-slate-500">CA annuel</p>
                <p className="text-xl font-bold text-blue-600">
                  {kpis.year_revenue?.toLocaleString('fr-FR')} €
                </p>
              </div>
              <div className="p-4 bg-violet-50 rounded-xl text-center">
                <p className="text-sm text-slate-500">TVA collectée (année)</p>
                <p className="text-xl font-bold text-violet-600">
                  {kpis.year_vat_collected?.toLocaleString('fr-FR')} €
                </p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-xl text-center">
                <p className="text-sm text-slate-500">Panier moyen</p>
                <p className="text-xl font-bold text-emerald-600">
                  {kpis.avg_invoice_amount?.toLocaleString('fr-FR')} €
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl text-center">
                <p className="text-sm text-slate-500">Clients actifs</p>
                <p className="text-xl font-bold text-amber-600">
                  {kpis.active_clients}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPremiumPage;
